import React, { useMemo, useState } from 'react';
import { DataManager } from '../../services/dataManager';

const SemanticMap = ({ keywords = {}, clientId, onUpdate }) => {
    const [isManaging, setIsManaging] = useState(false);
    const [selectedTags, setSelectedTags] = useState([]);
    const [targetTag, setTargetTag] = useState('');
    const [newTagInput, setNewTagInput] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [actionMessage, setActionMessage] = useState('');

    // Convert keywords object to sorted array
    const sortedKeywords = useMemo(() => {
        return Object.entries(keywords)
            .map(([word, data]) => ({
                word: data.display || word,
                rawWord: word,
                count: data.count || 0,
                category: data.category || 'general',
                subType: data.subType
            }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 60);
    }, [keywords]);

    const showNotification = (msg) => {
        setActionMessage(msg);
        setTimeout(() => setActionMessage(''), 3000);
    };

    // Unione tag selezionati
    const handleMerge = async () => {
        if (!clientId || selectedTags.length === 0 || !targetTag.trim()) return;
        setIsProcessing(true);
        try {
            const updated = await DataManager.mergeClientTags(clientId, selectedTags, targetTag.trim());
            showNotification(`✅ Unificati ${selectedTags.length} tag in "${targetTag.trim()}" su ${updated} sedute`);
            setSelectedTags([]);
            setTargetTag('');
            if (onUpdate) onUpdate();
        } catch (error) {
            console.error("Errore durante l'unione dei tag:", error);
            alert("Si è verificato un errore durante l'aggiornamento dei tag.");
        } finally {
            setIsProcessing(false);
        }
    };

    // Eliminazione tag selezionati
    const handleDeleteSelected = async () => {
        if (!clientId || selectedTags.length === 0) return;
        const confirmMsg = selectedTags.length === 1
            ? `Sei sicuro di voler eliminare il tag "${selectedTags[0]}" da tutte le sedute del cliente?`
            : `Sei sicuro di voler eliminare ${selectedTags.length} tag da tutte le sedute del cliente?`;
        
        if (!window.confirm(confirmMsg)) return;

        setIsProcessing(true);
        try {
            const updated = await DataManager.deleteClientTags(clientId, selectedTags);
            showNotification(`🗑️ Eliminati ${selectedTags.length} tag da ${updated} sedute`);
            setSelectedTags([]);
            if (onUpdate) onUpdate();
        } catch (error) {
            console.error("Errore durante l'eliminazione dei tag:", error);
            alert("Si è verificato un errore durante l'eliminazione dei tag.");
        } finally {
            setIsProcessing(false);
        }
    };

    // Eliminazione rapida di un singolo tag
    const handleDeleteSingle = async (e, tagToDelete) => {
        e.stopPropagation();
        if (!clientId || !tagToDelete) return;
        if (!window.confirm(`Eliminare il tag "${tagToDelete}" da tutti gli incontri?`)) return;

        setIsProcessing(true);
        try {
            const updated = await DataManager.deleteClientTags(clientId, [tagToDelete]);
            showNotification(`🗑️ Tag "${tagToDelete}" eliminato da ${updated} sedute`);
            setSelectedTags(prev => prev.filter(t => t !== tagToDelete));
            if (onUpdate) onUpdate();
        } catch (error) {
            console.error("Errore eliminazione tag:", error);
            alert("Errore durante l'eliminazione del tag.");
        } finally {
            setIsProcessing(false);
        }
    };

    // Aggiunta manuale di un nuovo tag alla seduta più recente
    const handleAddNewTag = async (e) => {
        e.preventDefault();
        if (!clientId || !newTagInput.trim()) return;
        const cleanTag = newTagInput.trim().replace(/^#/, '');

        setIsProcessing(true);
        try {
            const meetings = await DataManager.getClientMeetings(clientId, false);
            if (meetings.length === 0) {
                alert("Nessuna seduta registrata su cui aggiungere il tag.");
                return;
            }
            // Add to the most recent meeting
            const latestMeeting = meetings[meetings.length - 1];
            const currentTags = (latestMeeting.counselorTags || latestMeeting.tags || '')
                .split(',')
                .map(t => t.trim())
                .filter(Boolean);

            if (!currentTags.map(t => t.toLowerCase()).includes(cleanTag.toLowerCase())) {
                currentTags.push(cleanTag);
                const { FirebaseService } = await import('../../services/firebaseService');
                await FirebaseService.updateMeeting(latestMeeting.id, {
                    counselorTags: currentTags.join(', '),
                    tags: currentTags.join(', ')
                });
                await DataManager.analyzeAndUpdateKeywords(clientId);
                showNotification(`➕ Aggiunto tag "#${cleanTag}" all'ultima seduta`);
                setNewTagInput('');
                if (onUpdate) onUpdate();
            } else {
                alert(`Il tag "${cleanTag}" è già presente.`);
            }
        } catch (error) {
            console.error("Errore aggiunta tag:", error);
            alert("Errore durante l'aggiunta del tag.");
        } finally {
            setIsProcessing(false);
        }
    };

    const toggleSelection = (word) => {
        if (!isManaging) return;
        setSelectedTags(prev => prev.includes(word) ? prev.filter(w => w !== word) : [...prev, word]);
    };

    const selectAll = () => {
        setSelectedTags(sortedKeywords.map(k => k.rawWord));
    };

    const deselectAll = () => {
        setSelectedTags([]);
    };

    return (
        <div className="py-4">
            {clientId && (
                <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
                    <div>
                        <h3 className="text-2xl font-black text-slate-800 flex items-center gap-2">
                            <span>🗺️</span>
                            <span>Mappa Semantica</span>
                        </h3>
                        <p className="text-xs text-slate-500 mt-0.5">
                            {sortedKeywords.length} temi e parole chiave rilevati negli incontri
                        </p>
                    </div>

                    <div className="flex items-center gap-2">
                        <button 
                            onClick={() => { 
                                setIsManaging(!isManaging); 
                                setSelectedTags([]); 
                                setTargetTag(''); 
                            }}
                            className={`text-xs font-black uppercase px-4 py-2 rounded-full border transition-all flex items-center gap-1.5 shadow-sm ${
                                isManaging 
                                    ? 'bg-indigo-600 text-white border-indigo-700 shadow-md' 
                                    : 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100'
                            }`}
                        >
                            <span>{isManaging ? '✓ Chiudi Gestione' : '✏️ Gestisci, Unisci ed Elimina Tag'}</span>
                        </button>
                    </div>
                </div>
            )}

            {actionMessage && (
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold p-3 rounded-xl mb-4 animate-fade-in flex items-center justify-between">
                    <span>{actionMessage}</span>
                    <button onClick={() => setActionMessage('')} className="text-emerald-500 hover:text-emerald-700">✕</button>
                </div>
            )}

            {/* Management Panel */}
            {isManaging && (
                <div className="bg-indigo-50/80 border border-indigo-100 rounded-3xl p-5 mb-8 shadow-sm space-y-4 animate-fade-in">
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-indigo-100 pb-3">
                        <div>
                            <p className="text-xs font-bold text-indigo-950">
                                Modalità Gestione Parole & Tag
                            </p>
                            <p className="text-[11px] text-indigo-700">
                                Clicca sulle parole per selezionarle, quindi scegli se <strong>accorparle</strong> o <strong>eliminarle</strong>.
                            </p>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                            <span className="font-semibold text-indigo-900 bg-indigo-100 px-2.5 py-1 rounded-lg">
                                {selectedTags.length} selezionati
                            </span>
                            {selectedTags.length > 0 && (
                                <button
                                    onClick={deselectAll}
                                    className="text-xs text-indigo-600 hover:underline font-medium"
                                >
                                    Deseleziona tutti
                                </button>
                            )}
                            {selectedTags.length === 0 && sortedKeywords.length > 0 && (
                                <button
                                    onClick={selectAll}
                                    className="text-xs text-indigo-600 hover:underline font-medium"
                                >
                                    Seleziona tutti
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Actions Toolbar */}
                    <div className="grid md:grid-cols-2 gap-4 pt-1">
                        {/* 1. Unisci Tag */}
                        <div className="bg-white p-4 rounded-2xl border border-indigo-100 flex flex-col justify-between">
                            <span className="text-[10px] font-black uppercase tracking-wider text-indigo-900 mb-2 block">
                                🔗 1. Accorpa Tag Selezionati
                            </span>
                            <div className="flex gap-2">
                                <input 
                                    type="text" 
                                    placeholder="Nuovo nome tag unificato..." 
                                    value={targetTag}
                                    onChange={(e) => setTargetTag(e.target.value)}
                                    disabled={isProcessing || selectedTags.length === 0}
                                    className="text-xs px-3 py-2 rounded-xl border border-indigo-200 flex-1 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-50"
                                />
                                <button 
                                    onClick={handleMerge}
                                    disabled={isProcessing || selectedTags.length === 0 || !targetTag.trim()}
                                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-xs font-bold uppercase disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition whitespace-nowrap"
                                >
                                    {isProcessing ? 'Salvataggio...' : 'Unisci'}
                                </button>
                            </div>
                        </div>

                        {/* 2. Elimina Tag Selezionati */}
                        <div className="bg-white p-4 rounded-2xl border border-rose-100 flex flex-col justify-between">
                            <span className="text-[10px] font-black uppercase tracking-wider text-rose-900 mb-2 block">
                                🗑️ 2. Elimina Tag Selezionati
                            </span>
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-xs text-rose-700">
                                    {selectedTags.length === 0 ? "Seleziona almeno un tag" : `Rimuovi i ${selectedTags.length} tag selezionati`}
                                </span>
                                <button 
                                    onClick={handleDeleteSelected}
                                    disabled={isProcessing || selectedTags.length === 0}
                                    className="bg-rose-500 hover:bg-rose-600 text-white px-4 py-2 rounded-xl text-xs font-bold uppercase disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition whitespace-nowrap flex items-center gap-1"
                                >
                                    <span>🗑️</span>
                                    <span>{isProcessing ? 'Eliminazione...' : `Elimina (${selectedTags.length})`}</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* 3. Aggiungi nuovo tag manuale */}
                    <div className="pt-2 border-t border-indigo-100/70 flex items-center justify-between flex-wrap gap-2">
                        <span className="text-xs text-indigo-900 font-medium">Vuoi inserire un nuovo tema/tag?</span>
                        <form onSubmit={handleAddNewTag} className="flex gap-2">
                            <input
                                type="text"
                                placeholder="Nuovo tag (es: autostima)..."
                                value={newTagInput}
                                onChange={e => setNewTagInput(e.target.value)}
                                className="text-xs px-3 py-1.5 rounded-xl border border-indigo-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                            />
                            <button
                                type="submit"
                                disabled={isProcessing || !newTagInput.trim()}
                                className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs px-3 py-1.5 rounded-xl font-bold transition disabled:opacity-50"
                            >
                                + Aggiungi
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {sortedKeywords.length === 0 ? (
                <div className="text-center py-12 text-gray-400 bg-slate-50/50 rounded-2xl border border-dashed border-gray-200">
                    <span className="text-3xl block mb-2">🏷️</span>
                    <p className="text-sm font-bold text-gray-600">Nessun tag o parola chiave presente</p>
                    <p className="text-xs mt-1 text-gray-400">Aggiungi tag nei colloqui (#parola) o usa il pulsante di gestione in alto.</p>
                </div>
            ) : (
                <div
                    className="grid gap-3"
                    style={{
                        gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                        maxWidth: '1000px',
                        margin: '0 auto'
                    }}
                >
                    {sortedKeywords.map(({ word, rawWord, count }) => {
                        const isSelected = selectedTags.includes(rawWord);
                        return (
                            <div
                                key={rawWord}
                                onClick={() => toggleSelection(rawWord)}
                                className={`relative flex items-center justify-between p-3 rounded-2xl transition-all duration-200 group border ${
                                    isManaging ? 'cursor-pointer hover:shadow-md' : 'cursor-default'
                                } ${
                                    isSelected 
                                        ? 'bg-indigo-600 border-indigo-700 text-white shadow-md ring-2 ring-indigo-400' 
                                        : 'bg-indigo-50/60 border-indigo-100 hover:border-indigo-300 text-indigo-950'
                                }`}
                                style={{ minHeight: '60px' }}
                            >
                                {/* Word & Checkbox */}
                                <div className="flex items-center gap-2 pr-6">
                                    {isManaging && (
                                        <input 
                                            type="checkbox" 
                                            checked={isSelected}
                                            onChange={() => {}} // Handled by container click
                                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 pointer-events-none"
                                        />
                                    )}
                                    <span
                                        className={`select-none text-sm font-bold leading-tight ${isSelected ? 'text-white' : 'text-indigo-950'}`}
                                        title={`${word} (${count} occorrenze)`}
                                    >
                                        #{word}
                                    </span>
                                </div>

                                {/* Right Side: Counter & Quick Delete Button */}
                                <div className="flex items-center gap-1.5 absolute top-2 right-2">
                                    {isManaging && (
                                        <button
                                            type="button"
                                            onClick={(e) => handleDeleteSingle(e, rawWord)}
                                            title={`Elimina tag "${word}"`}
                                            className="w-5 h-5 rounded-full bg-white/80 hover:bg-rose-500 hover:text-white text-rose-500 flex items-center justify-center text-[10px] shadow-sm transition opacity-80 hover:opacity-100"
                                        >
                                            ✕
                                        </button>
                                    )}
                                    <span
                                        className={`rounded-full text-[10px] font-black leading-none px-1.5 py-0.5 shadow-sm ${
                                            isSelected ? 'bg-white text-indigo-700' : 'bg-indigo-600 text-white'
                                        }`}
                                    >
                                        {count}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default SemanticMap;
